// @flow
import { isRangeMonth, isRangeQuarter, isRangeYear } from 'date-functions';
import { format, addMonths, endOfMonth, endOfYear, startOfMonth } from 'date-fns';

const intervalTypes = {
  ALL: 'ALL',
  CUSTOM: 'CUSTOM',
  MONTH: 'MONTH',
  QUARTER: 'QUARTER',
  YEAR: 'YEAR',
};

type DateRange = {
  // A date string in the form "YYYY-MM-DD" representing the first day of the range, inclusive
  startDate: string,
  // A date string in the form "YYYY-MM-DD" representing the last day of the range, inclusive
  endDate: string,
};

type ReportingInterval = {
  dateRange: DateRange,
  intervalType: $Values<typeof intervalTypes>,
};

// This is the type that is saved and retrieved from local storage by the old main-app code. It
// stores weird values for the dates, so they're translated into reasonable representations for the
// new code.
type LegacyReportingInterval = {
  // A millisecond timestamp representing the first day of the date range, inclusive, at the
  // beginning of the day *in the local time zone*
  intervalFrom: number,
  // A millisecond timestamp representing the last day of the date range, inclusive, at the
  // beginning of the day *in the local time zone*
  intervalTo: number,
  intervalType: $Values<typeof intervalTypes>,
};

// Shared date format for use by date-fns/format
const dateFormat = 'YYYY-MM-DD';

// The dates stored by the legacy code are inconsistent. The intervalTo date is ignored in both
// setting and getting reporting intervals except when certain interval types are used. This
// function takes as little information as it can from the stored dates and returns a date range
// that represents the indicated interval. If the stored dates are invalid, returns undefined.
const convertLegacyReportingIntervalToDateRange = ({
  intervalFrom,
  intervalTo,
  intervalType,
}: LegacyReportingInterval): DateRange | void => {
  const intervalFromDate = new Date(intervalFrom);
  if (Number.isNaN(intervalFromDate.getTime())) {
    return undefined;
  }
  const intervalToDate = new Date(intervalTo);
  if (Number.isNaN(intervalToDate.getTime())) {
    return undefined;
  }

  // The intervalFrom date can't be trusted because it could have an incorrect day of month
  // depending on how the interval was changed, for example, from a custom date to an annual range
  // The start of the month is used for the start date for MONTH, QUARTER, and YEAR interval types
  let adjustedIntervalFromDate = intervalFromDate;
  if ([intervalTypes.MONTH, intervalTypes.QUARTER, intervalTypes.YEAR].includes(intervalType)) {
    adjustedIntervalFromDate = startOfMonth(intervalFromDate);
  }
  const startDate = format(adjustedIntervalFromDate, dateFormat);

  // Note that the end date is based on the intervalFrom date for MONTH, QUARTER, and YEAR interval
  // types and the intervalTo date is ignored
  let endDate;
  switch (intervalType) {
    case intervalTypes.MONTH:
      endDate = format(endOfMonth(startDate), dateFormat);
      break;
    case intervalTypes.QUARTER:
      endDate = format(endOfMonth(addMonths(startDate, 2)), dateFormat);
      break;
    case intervalTypes.YEAR:
      endDate = format(endOfYear(startDate), dateFormat);
      break;
    default:
      endDate = format(intervalToDate, dateFormat);
      break;
  }

  return { startDate, endDate };
};

// Returns a millisecond timestamp in the local time zone at the beginning of the day representing
// the given string in the form "YYYY-MM-DD". If the date is invalid, returns NaN.
const convertLocalDateStringToLegacyTimestamp = (localDateString: string): number => {
  // Use local time instead of UTC by omitting the Z at the end
  const localDateTimeAtStartOfDay = new Date(`${localDateString}T00:00:00`);
  return localDateTimeAtStartOfDay.getTime();
};

const getReportingIntervalKey = (clientId: string) => `reportingInterval${clientId}`;

/**
 * @param clientId - the ID of the client whose reporting interval should be retrieved
 * @return the saved reporting interval or undefined if none is saved or the saved data is invalid
 */
export const loadReportingInterval = (clientId: string): ReportingInterval | void => {
  const reportingIntervalKey = getReportingIntervalKey(clientId);
  const savedReportingIntervalString = localStorage.getItem(reportingIntervalKey);
  if (!savedReportingIntervalString) {
    return undefined;
  }
  let savedReportingInterval: LegacyReportingInterval;
  try {
    savedReportingInterval = JSON.parse(savedReportingIntervalString);
  } catch (err) {
    // There was an error parsing the saved JSON, so it's the same as if nothing was saved
    return undefined;
  }
  if (typeof savedReportingInterval !== 'object' || savedReportingInterval === null) {
    return undefined;
  }
  const { intervalType } = savedReportingInterval;
  if (!Object.keys(intervalTypes).includes(intervalType)) {
    return undefined;
  }
  const dateRange = convertLegacyReportingIntervalToDateRange(savedReportingInterval);
  if (!dateRange) {
    return undefined;
  }
  return { dateRange, intervalType };
};

/**
 * @param clientId - the ID of the client whose reporting interval should be persisted
 * @param dateRange - the date range of the reporting interval to persist
 * @throws if reportingInterval contains invalid values for any of its properties
 */
export const saveReportingInterval = (clientId: string, { endDate, startDate }: DateRange) => {
  const intervalFrom = convertLocalDateStringToLegacyTimestamp(startDate);
  if (Number.isNaN(intervalFrom)) {
    throw new Error(
      `dateRange.startDate should be a valid date string in the form YYYY-MM-DD but got: ${startDate}`,
    );
  }
  const intervalTo = convertLocalDateStringToLegacyTimestamp(endDate);
  if (Number.isNaN(intervalTo)) {
    throw new Error(
      `dateRange.endDate should be a valid date string in the form YYYY-MM-DD but got: ${endDate}`,
    );
  }
  let intervalType;
  if (isRangeMonth(startDate, endDate)) {
    intervalType = intervalTypes.MONTH;
  } else if (isRangeQuarter(startDate, endDate)) {
    intervalType = intervalTypes.QUARTER;
  } else if (isRangeYear(startDate, endDate)) {
    intervalType = intervalTypes.YEAR;
  } else {
    intervalType = intervalTypes.CUSTOM;
  }
  const legacyReportingInterval: LegacyReportingInterval = {
    intervalFrom,
    intervalTo,
    intervalType,
  };
  const serializedLegacyReportingInterval = JSON.stringify(legacyReportingInterval);
  const reportingIntervalKey = getReportingIntervalKey(clientId);
  localStorage.setItem(reportingIntervalKey, serializedLegacyReportingInterval);
};
